import { Divider, Grid, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import type {
  ChartReportValidationResponse,
  MeasuresValidationResult,
  PivotReportValidationResponse,
  ReportValidationResponse,
  TabularReportValidationResponse,
  ListReportValidationResponse,
  TileReportValidationResponse,
} from "../../../../../../../api/types/svTypes.ts";
import useUserContext from "../../../../../../../context/useUserContext";
import type { SelectedCompany, SelectedCompanyReport } from "../hooks/SelectedCompany";
import { DimensionValidation, MeasureValidation } from "./DimensionValidation";
import { ConditionsValidation, FieldsContainer, FieldsValidation } from "./FieldsValidation";
import Group from "./Group";
import NoCompanySelected from "./NoCompanySelected";

interface Props {
  company: SelectedCompany | undefined;
  companyReport: SelectedCompanyReport | undefined;
  updateCompanyReport: (report: SelectedCompanyReport, changes: Partial<SelectedCompanyReport>) => void;
}
export function ValidationDetailsContainer({ company, companyReport, updateCompanyReport }: Props) {
  const { clients } = useUserContext();

  const companyInfo = useMemo(() => {
    return clients.find((c) => c.clientCode === companyReport?.companyCode);
  }, [clients, companyReport]);

  if (!companyReport || !company) {
    return <NoCompanySelected />;
  }

  if (isReportWaitingOrValidating(companyReport)) {
    return <InlineLoader />;
  }

  if (companyReport?.error) {
    return (
      <Stack sx={{ alignItems: "center", justifyContent: "center", width: "100%" }}>
        <Typography color="error">An error occurred while validating the report</Typography>
      </Stack>
    );
  }

  if (companyReport.result?.hasRequiredDataset === false) {
    return (
      <Stack sx={{ textAlign: "center", width: "100%", justifyContent: "center" }}>
        <Typography color="error" sx={{ px: 2 }}>
          {`Dataset ${companyReport.result?.requiredDataSetName ?? ""} is missing in the target company.`}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ width: "100%" }}>
      <Stack spacing={0.5} useFlexGap sx={{ py: 2, px: 2.5 }}>
        <Typography variant="subtitle1">{companyReport.report.name}</Typography>
        <Typography color="secondary">{companyInfo?.title}</Typography>
      </Stack>
      <Divider />
      <Stack spacing={2} useFlexGap sx={{ py: 2, px: 2.5 }}>
        <Typography color="secondary">Choose the group to which the report will be copied</Typography>
        <Group company={company} companyReport={companyReport} updateCompanyReport={updateCompanyReport} />
        <Typography color="secondary">Fields that will be copied with the report</Typography>
      </Stack>
      <Grid container sx={{ flex: 1, position: "relative", width: "100%" }}>
        <Grid
          container
          sx={{ flex: 1, gap: 2, px: 2.5, position: "absolute", height: "100%", width: "100%", overflowY: "auto" }}
        >
          {companyReport.result && <ValidatedFieldsContainer validationResult={companyReport.result} />}
        </Grid>
      </Grid>
    </Stack>
  );
}

export function ValidatedFieldsContainer({ validationResult }: { validationResult: ReportValidationResponse }) {
  if (validationResult.reportType === "Pivot") {
    return <PivotValidationContainer validationResult={validationResult} />;
  }
  if (validationResult.reportType === "Tabular") {
    return <TabularValidationContainer validationResult={validationResult} />;
  }
  if (validationResult.reportType === "Chart") {
    return <ChartValidationContainer validationResult={validationResult} />;
  }
  if (validationResult.reportType === "Tile") {
    return <TileValidationContainer validationResult={validationResult} />;
  }
  if (validationResult.reportType === "List") {
    return <ListValidationContainer validationResult={validationResult} />;
  }
  return null;
}

function PivotValidationContainer({ validationResult }: { validationResult: PivotReportValidationResponse }) {
  return (
    <Stack spacing={1.5} useFlexGap sx={{ pb: 1, width: "100%" }}>
      <ConditionsValidation conditions={validationResult.conditions} />
      <FieldsValidation caption="Rows" fields={validationResult.rows} />
      <FieldsValidation caption="Columns" fields={validationResult.columns} />
      <MeasuresValidation measures={validationResult.measures} />
    </Stack>
  );
}

function TabularValidationContainer({ validationResult }: { validationResult: TabularReportValidationResponse }) {
  return (
    <Stack spacing={1.5} useFlexGap sx={{ pb: 1, width: "100%" }}>
      <ConditionsValidation conditions={validationResult.conditions} />
      <FieldsContainer caption="Fields">
        {validationResult.fields?.map((c) => {
          if (c.dimension !== undefined) {
            return <DimensionValidation key={c.dimension.guid} field={c.dimension} sx={{ py: 0.5 }} />;
          } else if (c.measure !== undefined) {
            return <MeasureValidation key={c.measure.guid} field={c.measure} />;
          }
          return null;
        })}
      </FieldsContainer>
    </Stack>
  );
}

function ChartValidationContainer({ validationResult }: { validationResult: ChartReportValidationResponse }) {
  return (
    <Stack spacing={1.5} useFlexGap sx={{ pb: 1, width: "100%" }}>
      <ConditionsValidation conditions={validationResult.conditions} />
      <FieldsValidation caption="Arguments" fields={validationResult.arguments} />
      <MeasuresValidation measures={validationResult.measures} />
    </Stack>
  );
}

function TileValidationContainer({ validationResult }: { validationResult: TileReportValidationResponse }) {
  return (
    <Stack spacing={1.5} useFlexGap sx={{ pb: 1, width: "100%" }}>
      <ConditionsValidation conditions={validationResult.conditions} />
      <FieldsValidation caption="Fields" fields={validationResult.fields} />
    </Stack>
  );
}

function ListValidationContainer({ validationResult }: { validationResult: ListReportValidationResponse }) {
  return (
    <Stack spacing={1.5} useFlexGap sx={{ pb: 1, width: "100%" }}>
      <ConditionsValidation conditions={validationResult.conditions} />
      <FieldsValidation caption="Fields" fields={validationResult.fields} />
    </Stack>
  );
}

function MeasuresValidation({ measures }: { measures?: MeasuresValidationResult[] | null | undefined }) {
  if (!measures) return null;
  return (
    <FieldsContainer caption="Measures">
      {measures.map((m) => (
        <MeasureValidation key={m.guid} field={m} />
      ))}
    </FieldsContainer>
  );
}

function isReportWaitingOrValidating(report: SelectedCompanyReport | undefined) {
  return report?.validating || (!report?.validated && !report?.validating && !report?.error);
}
